/**
 * Burst grouping for the audit event list (pure, unit-tested).
 *
 * A burst is a run of >= BURST_MIN_RUN CONSECUTIVE events (in list order) with the same
 * actor key + event type + client, where each event is within BURST_MAX_GAP_MS of the
 * previous one — e.g. someone deleting 43 deposits in a minute. Bursts collapse into one
 * expandable row; shorter runs stay as individual rows. Grouping is per loaded page.
 */
import type { AuditEventEnvelope } from '@/types';
import { actorKeyOf } from './auditMeta';

export const BURST_MIN_RUN = 3;
export const BURST_MAX_GAP_MS = 120 * 1000;

type BurstEventLike = Pick<AuditEventEnvelope, 'id' | 'ts' | 'eventType' | 'clientId' | 'actorUserId' | 'actorEmail' | 'actorType'> & {
  actorKey?: string | null;
};

export type EventListItem<T extends BurstEventLike = AuditEventEnvelope> =
  | { kind: 'single'; key: string; event: T }
  | { kind: 'burst'; key: string; events: T[] };

function sameBurst(a: BurstEventLike, b: BurstEventLike, maxGapMs: number): boolean {
  if (actorKeyOf(a) !== actorKeyOf(b)) return false;
  if (a.eventType !== b.eventType) return false;
  if ((a.clientId ?? null) !== (b.clientId ?? null)) return false;
  const gap = Math.abs(Date.parse(a.ts) - Date.parse(b.ts));
  return Number.isFinite(gap) && gap <= maxGapMs;
}

export function groupBursts<T extends BurstEventLike>(
  events: readonly T[],
  opts: { minRun?: number; maxGapMs?: number } = {}
): EventListItem<T>[] {
  const minRun = opts.minRun ?? BURST_MIN_RUN;
  const maxGapMs = opts.maxGapMs ?? BURST_MAX_GAP_MS;
  const items: EventListItem<T>[] = [];
  let run: T[] = [];

  const flush = () => {
    if (run.length >= minRun) {
      // Keyed by the OLDEST row (last in the newest-first run): a refresh that prepends newer
      // rows to the burst keeps the same key, so its expanded/collapsed state survives.
      items.push({ kind: 'burst', key: `burst:${run[run.length - 1].id}`, events: run });
    } else {
      for (const e of run) items.push({ kind: 'single', key: e.id, event: e });
    }
    run = [];
  };

  for (const e of events) {
    if (run.length && !sameBurst(run[run.length - 1], e, maxGapMs)) flush();
    run.push(e);
  }
  flush();
  return items;
}
